/**
 * Receipt HTML Renderer — Client-side version of receipt-to-HTML conversion.
 *
 * Converts processed template JSON (from processReceiptTemplate) into an
 * HTML string suitable for Electron's native printDirect or printToPdf.
 * Runs in the renderer process.
 */

const PAPER_WIDTHS = {
  80: "80mm",
  58: "58mm",
  76: "76mm",
  112: "112mm",
};

function escapeHtml(str) {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildStyles(element) {
  const styles = [];
  const s = element.style || [];
  if (s.includes("bold") || element.bold) styles.push("font-weight:bold");
  if (s.includes("underline") || element.underline) styles.push("text-decoration:underline");
  if (s.includes("large") || element.large) styles.push("font-size:1.4em");
  if (s.includes("small") || element.small) styles.push("font-size:0.85em");

  const align = element.alignment || element.align || "left";
  styles.push(`text-align:${align}`);

  return styles.join(";");
}

function renderTextElement(el) {
  const style = buildStyles(el);
  const content = escapeHtml(el.content || "");
  return `<div class="receipt-text" style="${style}">${content}</div>`;
}

function renderLineElement() {
  return `<hr class="receipt-line" />`;
}

function renderSpacingElement(el) {
  const height = el.height || el.lines || 1;
  return `<div class="receipt-spacing" style="height:${height * 1.2}em"></div>`;
}

function renderTableElement(el) {
  const columns = el.columns || [];
  const rows = Array.isArray(el.data) ? el.data : [];

  if (columns.length === 0 || rows.length === 0) return "";

  let html = `<table class="receipt-table">`;

  if (el.showHeader !== false && columns.some((c) => c.header)) {
    html += `<thead><tr>`;
    for (const col of columns) {
      const align = col.alignment || "left";
      const width = col.width ? `width:${col.width}%` : "";
      html += `<th style="text-align:${align};${width}">${escapeHtml(col.header || "")}</th>`;
    }
    html += `</tr></thead>`;
  }

  html += `<tbody>`;
  const colKeys = columns.map((_, idx) => {
    const mapping = el.dataMapping || {};
    return Object.keys(mapping)[idx] || `col${idx}`;
  });

  for (const row of rows) {
    const rowStyle = row.style && row.style.includes("bold") ? 'style="font-weight:bold"' : "";
    html += `<tr ${rowStyle}>`;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const key = colKeys[i];
      const align = col.alignment || "left";
      const width = col.width ? `width:${col.width}%` : "";
      const value = row[key] !== undefined && row[key] !== null ? row[key] : "";
      html += `<td style="text-align:${align};${width}">${escapeHtml(value)}</td>`;
    }
    html += `</tr>`;
  }
  html += `</tbody></table>`;

  return html;
}

function renderBarcodeElement(el) {
  const content = escapeHtml(el.content || "");
  return `<div class="receipt-barcode" style="text-align:center;font-family:'Libre Barcode 128',monospace;font-size:36px;letter-spacing:2px">${content}</div>
    <div style="text-align:center;font-size:0.8em">${content}</div>`;
}

function renderQrCodeElement(el) {
  const content = escapeHtml(el.content || "");
  return `<div class="receipt-qrcode" style="text-align:center;padding:8px">
    <div style="border:1px solid #000;display:inline-block;padding:8px;font-size:0.7em">[QR: ${content}]</div>
  </div>`;
}

function renderElement(element) {
  switch (element.type) {
    case "text":
      return renderTextElement(element);
    case "line":
    case "divider":
      return renderLineElement();
    case "spacing":
      return renderSpacingElement(element);
    case "table":
      return renderTableElement(element);
    case "barcode":
      return renderBarcodeElement(element);
    case "qrcode":
      return renderQrCodeElement(element);
    default:
      return "";
  }
}

/**
 * Convert processed template JSON to a full HTML document for printing.
 * @param {Object} processedTemplate - Output of processReceiptTemplate()
 * @param {number} paperWidth - Paper width in mm (default 80)
 * @returns {string} Complete HTML document string
 */
export function templateToHtml(processedTemplate, paperWidth = 80) {
  const width = PAPER_WIDTHS[paperWidth] || `${paperWidth}mm`;
  const elements = processedTemplate?.elements || [];

  const body = elements.map(renderElement).join("\n");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  @page {
    size: ${width} auto;
    margin: 0;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    line-height: 1.3;
    width: ${width};
    padding: 4mm 2mm;
    color: #000;
    -webkit-print-color-adjust: exact;
  }
  .receipt-text {
    padding: 1px 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .receipt-line {
    border: none;
    border-top: 1px dashed #000;
    margin: 4px 0;
  }
  .receipt-table {
    width: 100%;
    border-collapse: collapse;
    margin: 2px 0;
  }
  .receipt-table th {
    border-bottom: 1px solid #000;
    padding: 2px 1px;
    font-size: 0.9em;
  }
  .receipt-table td {
    padding: 1px;
    vertical-align: top;
    font-size: 0.95em;
  }
  .receipt-barcode {
    margin: 6px 0;
  }
</style>
</head>
<body>
${body}
</body>
</html>`;
}

export default templateToHtml;
